import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PlusIcon } from '@heroicons/react/20/solid';
import {
  DocumentArrowUpIcon,
  DocumentTextIcon,
  LinkIcon,
  MicrophoneIcon
} from '@heroicons/react/24/outline';
import StandardTable from '../../ui/molecules/StandardTable';
import TablePageLayout from '../../ui/molecules/TablePageLayout';
import Pagination from '../../ui/molecules/Pagination';
import { formatDisplayDateTime } from '../../utils/dateFormatting';
import { describePodcastError, listPodcasts } from './podcastApi';
import { PODCASTS_PATH, POLL_INTERVAL_MS, getJobTitle, getSourceDetail, getSourceType, isJobActive } from './podcastJob';
import { languageLabel, lengthLabel, toneLabel } from './podcastLabels';
import PodcastStatus from './PodcastStatus';

const PAGE_SIZE = 25;
const SOURCE_ICONS = { url: LinkIcon, text: DocumentTextIcon, file: DocumentArrowUpIcon };

const SkeletonRow = React.memo(() => (
  <tr className="animate-pulse">
    <td className="whitespace-nowrap py-5 pl-4 pr-3 sm:pl-0">
      <div className="h-4 bg-gray-200 rounded w-48"></div>
    </td>
    <td className="whitespace-nowrap px-3 py-5">
      <div className="h-4 bg-gray-200 rounded w-24"></div>
    </td>
    <td className="whitespace-nowrap px-3 py-5">
      <div className="h-4 bg-gray-200 rounded w-12"></div>
    </td>
    <td className="whitespace-nowrap px-3 py-5">
      <div className="h-4 bg-gray-200 rounded w-16"></div>
    </td>
    <td className="whitespace-nowrap px-3 py-5">
      <div className="h-4 bg-gray-200 rounded w-12"></div>
    </td>
    <td className="whitespace-nowrap px-3 py-5">
      <div className="h-6 bg-gray-200 rounded-full w-16"></div>
    </td>
    <td className="whitespace-nowrap px-3 py-5">
      <div className="h-4 bg-gray-200 rounded w-28"></div>
    </td>
  </tr>
));

const EmptyState = ({ onPrimaryClick }) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center h-full text-center py-24" data-testid="podcasts-empty">
      <div className="mx-auto flex h-32 w-32 items-center justify-center rounded-full bg-primary-50">
        <MicrophoneIcon className="h-16 w-16 text-primary-500" aria-hidden="true" />
      </div>
      <h1 className="text-3xl font-medium text-gray-900 mt-6">{t('podcasts.emptyState.title')}</h1>
      <p className="mt-1 text-base text-gray-800 opacity-80 max-w-xl">{t('podcasts.emptyState.description')}</p>
      <div className="mt-6">
        <button type="button" onClick={onPrimaryClick} className="btn btn-md btn-rounded btn-primary">
          <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
          {t('podcasts.emptyState.button')}
        </button>
      </div>
    </div>
  );
};

const PodcastsListPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const primaryClick = useCallback(() => navigate(`${PODCASTS_PATH}/create`), [navigate]);
  const openJob = useCallback((job) => navigate(`${PODCASTS_PATH}/${job.id}`), [navigate]);

  const fetchJobs = useCallback(async ({ showLoading = false } = {}) => {
    if (showLoading) setLoading(true);
    try {
      const items = await listPodcasts();
      if (!mountedRef.current) return;
      setJobs(items);
      setError(null);
    } catch (err) {
      if (mountedRef.current) setError(describePodcastError(err, t));
    } finally {
      if (mountedRef.current && showLoading) setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchJobs({ showLoading: true });
  }, [fetchJobs]);

  const hasActiveJobs = useMemo(() => jobs.some(isJobActive), [jobs]);

  // Odświeżanie listy co 2 s, tylko dopóki są aktywne zadania (queued / running).
  useEffect(() => {
    if (!hasActiveJobs) return undefined;
    let inFlight = false;
    const refresh = async () => {
      if (inFlight || document.visibilityState === 'hidden') return;
      inFlight = true;
      try {
        await fetchJobs();
      } finally {
        inFlight = false;
      }
    };
    const intervalId = window.setInterval(refresh, POLL_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [fetchJobs, hasActiveJobs]);

  const columns = useMemo(() => [
    {
      key: 'title',
      title: t('podcasts.list.columns.title'),
      render: (job) => (
        <div className="max-w-[320px]">
          <div className="font-medium text-gray-900 truncate" title={getJobTitle(job) || ''}>
            {getJobTitle(job) || t('podcasts.result.untitled')}
          </div>
        </div>
      ),
      headerClassName: 'py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0',
      cellClassName: 'py-5 pl-4 pr-3 text-sm sm:pl-0'
    },
    {
      key: 'source',
      title: t('podcasts.list.columns.source'),
      render: (job) => {
        const type = getSourceType(job);
        const Icon = SOURCE_ICONS[type] || LinkIcon;
        const detail = getSourceDetail(job);
        return (
          <div className="flex items-center gap-2 max-w-[220px]">
            <Icon className="h-4 w-4 flex-shrink-0 text-gray-400" aria-hidden="true" />
            <div className="min-w-0">
              <div className="text-gray-700">{t(`podcasts.sourceTypes.${type}`)}</div>
              {detail && <div className="text-xs text-gray-500 truncate" title={detail}>{detail}</div>}
            </div>
          </div>
        );
      }
    },
    {
      key: 'language',
      title: t('podcasts.list.columns.language'),
      render: (job) => languageLabel(job.language) || '-'
    },
    {
      key: 'tone',
      title: t('podcasts.list.columns.tone'),
      render: (job) => toneLabel(job, t) || '-'
    },
    {
      key: 'length',
      title: t('podcasts.list.columns.length'),
      render: (job) => lengthLabel(job, t) || '-'
    },
    {
      key: 'status',
      title: t('podcasts.list.columns.status'),
      render: (job) => <PodcastStatus job={job} />,
      cellClassName: 'px-3 py-5 text-sm text-gray-500'
    },
    {
      key: 'createdAt',
      title: t('common.creationDate'),
      render: (job) => (job.createdAt ? formatDisplayDateTime(job.createdAt, i18n.language) : '-')
    }
  ], [t, i18n.language]);

  const totalPages = Math.max(1, Math.ceil(jobs.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageJobs = useMemo(
    () => jobs.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [jobs, currentPage, pageSize]
  );
  const hasItems = jobs.length > 0;

  return (
    <TablePageLayout
      title={t('podcasts.title')}
      description={t('podcasts.description')}
      primaryAction={hasItems ? t('podcasts.createNew') : undefined}
      onPrimaryAction={hasItems ? primaryClick : undefined}
    >
      {error && (
        <div role="alert" className="mb-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 flex items-center justify-between gap-3">
          <span>
            <span className="font-medium">{t('podcasts.list.loadError')}</span> {error}
          </span>
          <button type="button" className="btn btn-secondary btn-xs" onClick={() => fetchJobs({ showLoading: true })}>
            {t('podcasts.list.retry')}
          </button>
        </div>
      )}
      {loading || hasItems ? (
        <StandardTable
          columns={columns}
          data={pageJobs}
          loading={loading}
          SkeletonRow={SkeletonRow}
          EmptyState={EmptyState}
          emptyStateProps={{ onPrimaryClick: primaryClick }}
          onRowClick={openJob}
          data-testid="podcasts-table"
        />
      ) : (
        !error && <EmptyState onPrimaryClick={primaryClick} />
      )}
      {!loading && jobs.length > 10 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setPage}
          itemsPerPage={pageSize}
          totalCount={jobs.length}
          onItemsPerPageChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
        />
      )}
    </TablePageLayout>
  );
};

export default PodcastsListPage;
